import React, { useEffect, useMemo, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { Overlay } from '~/ui/overlay/Overlay';
import { Modal } from '~/ui/modal/Modal';
import CloseIcon from '~/assets/icons/close.svg';
import { EditBtnIcon } from '~/editor/buttons/EditBtn';
import FilePickerV2 from '~/editor/filePicker/FilePicker_v2';
import emptyImg from '~/training/sequence/templates/items/doorCard/empty.png';
import { useEditorStore, EditorMediaFile } from '~/shell/Store';
import { escapeHtml, stripHtml, sanitizeBodyForSave } from '~/lib/sanitization';
import { useToastStore } from '~/ui/toast/toastStore';
import { ToastSeverity } from '~/ui/toast/types';

const StyledModal = styled(Modal)`
  border-radius: 24px !important;
`;
const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px; /* reduced spacing */
  @media (max-height: 900px) {
    margin-bottom: 10px;
  }
  @media (max-height: 780px) {
    margin-bottom: 8px;
  }
`;
const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    opacity: 0.7;
  }
`;
const StyledCloseIcon = styled(CloseIcon)`
  width: 20px;
  height: 20px;
`;
const Title = styled.h3`
  margin: 0;
  font-family: Raleway;
  font-size: 18px;
  font-weight: 600;
  text-align: left;
  @media (max-height: 900px) {
    font-size: 17px;
  }
  @media (max-height: 780px) {
    font-size: 16px;
  }
`;
const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-family: Raleway;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  @media (max-height: 900px) {
    font-size: 13px;
    margin-bottom: 6px;
  }
  @media (max-height: 780px) {
    font-size: 12.5px;
    margin-bottom: 4px;
  }
`;
const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  font-family: Raleway;
  font-size: 14px;
  margin-bottom: 16px;
  background: #f8f9fa;
  &:focus {
    outline: none;
    border-color: #9738ff;
    background: white;
  }
  &::placeholder {
    color: #999;
  }
  @media (max-height: 900px) {
    padding: 10px 14px;
    font-size: 13px;
    margin-bottom: 14px;
  }
  @media (max-height: 780px) {
    padding: 8px 12px;
    font-size: 12.5px;
    margin-bottom: 12px;
  }
`;
const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 12px 16px;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  font-family: Raleway;
  font-size: 14px;
  margin-bottom: 16px;
  background: #f8f9fa;
  resize: vertical;
  &:focus {
    outline: none;
    border-color: #9738ff;
    background: white;
  }
  &::placeholder {
    color: #999;
  }
  @media (max-height: 900px) {
    min-height: 110px;
    padding: 10px 14px;
    font-size: 13px;
    margin-bottom: 14px;
  }
  @media (max-height: 780px) {
    min-height: 100px;
    padding: 8px 12px;
    font-size: 12.5px;
    margin-bottom: 12px;
  }
`;
const ImageBox = styled.div`
  width: 70%;
  height: 180px; /* taller preview */
  border: 2px dashed #d4d8dd;
  border-radius: 12px;
  background: #fafbfc;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  margin-left: auto;
  margin-right: auto;
`;
const ImagePreview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;
const EditImageButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  margin: 0;
  padding: 5px;
  background-color: #f3ecff;
  color: #9738ff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    background-color 0.18s ease,
    color 0.18s ease;

  svg {
    width: 18px;
    height: 18px;
    display: block;
  }
  svg path {
    fill: currentColor !important;
    transition: fill 0.18s ease;
  }

  &:hover {
    background-color: #9738ff;
    color: #fff;
    svg path {
      fill: #fff !important;
    }
  }

  &.active {
    background-color: #9845fb;
    color: #fff;
    svg path {
      fill: #fff !important;
    }
  }
`;
const ButtonRow = styled.div`
  display: flex;
`;
const SubmitButton = styled.button`
  width: 100%;
  padding: 10px 20px;
  border-radius: 10px;
  font-family: Raleway;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: #9738ff;
  color: white;
  letter-spacing: 0.3px;
  transition: background 0.15s ease;
  &:hover {
    background: #7a2dcc;
  }
  &:active {
    background: #5d22a3;
  }
  @media (max-height: 900px) {
    padding: 9px 18px;
    font-size: 14.5px;
  }
  @media (max-height: 780px) {
    padding: 8px 16px;
    font-size: 14px;
  }
`;

/**
 * The stored body with its heading set to `heading`, every other byte kept:
 * the heading is the body's first bold segment (see the dialog's loading).
 */
const bodyWithHeading = (storedBody: string, heading: string) => {
  const match = storedBody.match(/<b>.*?<\/b>(<br\s*\/?><br\s*\/?>)?/i);
  if (!match || match.index === undefined) {
    return heading ? `<b>${heading}</b><br><br>${storedBody}` : storedBody;
  }
  const before = storedBody.slice(0, match.index);
  const after = storedBody.slice(match.index + match[0].length);
  return heading
    ? `${before}<b>${heading}</b>${match[1] ?? ''}${after}`
    : `${before}${after}`;
};

/** The card's title column takes up to 512 characters (as in Edit Door). */
const CARD_TITLE_MAX_LENGTH = 512;
/** The title in the body is the body's; the dialog keeps it to a heading. */
const BODY_TITLE_MAX_LENGTH = 120;

/** Names for the card's own title and the title in its body, where they differ. */
export interface FlipCardTitleLabels {
  cardTitle: string;
  cardTitlePlaceholder: string;
  bodyTitle: string;
}

interface FlipCardEditModalProps {
  open: boolean;
  onClose: () => void;
  cardId: string | number;
  isNew?: boolean;
  defaultImageUrl?: string;
  separateTitleAndBody?: boolean;
  showSubtitle?: boolean;
  showTitle?: boolean;
  /**
   * The card's own title is its own (learners see it apart from the body's
   * heading), so the heading is never saved as it, with or without a field
   * for it (showTitle).
   */
  cardOwnsTitle?: boolean;
  /** With showTitle: what learners know each title as (e.g. front and back). */
  titleLabels?: FlipCardTitleLabels;
  /**
   * A question card shows only its picture and, on its back, its first
   * answer's feedback: the panel edits those, never the question's own text.
   */
  questionCard?: boolean;
  /** The answer whose feedback the back of the question card shows. */
  answerFeedbackId?: string | number;
}

const FlipCardEditModal: React.FC<FlipCardEditModalProps> = ({
  open,
  onClose,
  cardId,
  isNew = false,
  defaultImageUrl,
  separateTitleAndBody = false,
  showSubtitle = false,
  showTitle = false,
  cardOwnsTitle = false,
  titleLabels = {
    cardTitle: 'Card Title',
    cardTitlePlaceholder: 'Enter card title (e.g. author name)',
    bodyTitle: 'Title (in body)'
  },
  questionCard = false,
  answerFeedbackId
}) => {
  const {
    sequence,
    updateSubItemBatch,
    markContentEdited,
    updateFile,
    contentEditable,
    addSubItem
  } = useEditorStore();
  const [title, setTitle] = useState('');
  const [cardTitle, setCardTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [body, setBody] = useState('');
  // A question card's answer feedback, as loaded: sent back only if changed.
  const [feedback, setFeedback] = useState('');
  const loadedFeedbackRef = useRef('');
  const [imageUrl, setImageUrl] = useState<string | undefined>();
  const [isSaving, setIsSaving] = useState(false);
  const closeUnlessSaving = () => {
    if (!isSaving) onClose();
  };
  // Loaded once per opening: a slide refresh while the dialog is open must not replace what the user typed.
  const cardLoadedRef = useRef(false);
  // Where the title field is the title in the body, the card's own title is
  // saved only when the author changes that field: editing anything else must
  // not overwrite a front title learners see on its own.
  const titleFromBody = !separateTitleAndBody && !showTitle && !cardOwnsTitle;
  const loadedBodyTitleRef = useRef('');
  // The body as stored, and its description as the dialog shows it: the
  // dialog shows plain text, so an untouched body is never sent back (that
  // would drop its links and formatting).
  const loadedBodyRef = useRef('');
  const loadedDescriptionRef = useRef('');
  // The card's own title and its subtitle as stored (HTML), and as the dialog
  // shows them: one the author leaves is sent back exactly as stored.
  const storedTextRef = useRef<{ title?: string; subtitle?: string }>({});
  const shownTextRef = useRef({ title: '', subtitle: '' });

  interface ImageFile extends EditorMediaFile {
    url?: string;
  }

  interface CardLike {
    body?: string;
    title?: string;
    subtitle?: string;
    values?: { body?: string };
    value?: string;
    __typename?: string;
  }

  const [selectedImageFile, setSelectedImageFile] = useState<
    ImageFile | undefined
  >();

  const [showFilePicker, setShowFilePicker] = useState(false);
  const initialImageIdRef = useRef<string | number | undefined>(undefined);

  // only allow when content is editable
  if (!contentEditable) return null;

  const cardItem = useMemo(() => {
    if (!sequence?.items) return undefined;
    const stack = [...sequence.items];
    while (stack.length) {
      const it = stack.shift();
      if (!it) break;
      if (it.id?.toString() === cardId.toString()) return it;
      if (it.items) stack.push(...it.items);
    }
    return undefined;
  }, [sequence, cardId]);

  // Helper to strip HTML tags for editing plain text, preserving line breaks
  // We explicitly convert <br> tags to placeholders before decoding so that
  // sequential <br><br> become two newline characters (representing a blank line).
  useEffect(() => {
    if (cardLoadedRef.current) return;

    if (isNew) {
      cardLoadedRef.current = true;
      setTitle('');
      setCardTitle('');
      setSubtitle('');
      setBody('');
      if (defaultImageUrl) {
        setImageUrl(defaultImageUrl);
      } else {
        setImageUrl(emptyImg as unknown as string);
      }
      initialImageIdRef.current = undefined;
      return;
    }

    if (cardItem) {
      cardLoadedRef.current = true;
      // Always derive title from bolded segment at start of body (if present), not from the card's title field
      const rawBodyHtml =
        (cardItem as CardLike).body ||
        (cardItem.values as { body?: string } | undefined)?.body ||
        (cardItem as CardLike).value ||
        '';

      const firstBoldPattern = /<b>(.*?)<\/b>/i; // first bold occurrence anywhere
      let extractedTitle = '';
      let workingBodyHtml = rawBodyHtml;
      const match = rawBodyHtml.match(firstBoldPattern);
      if (match) {
        extractedTitle = stripHtml(match[1]);
        const removePattern = new RegExp(
          `<b>${match[1].replace(
            /[-/\\^$*+?.()|[\]{}]/g,
            (r: string) => '\\' + r
          )}<\\/b>(?:<br\\s*\\/?><br\\s*\\/?>)?`,
          'i'
        );
        workingBodyHtml = workingBodyHtml.replace(removePattern, '');
      }
      const storedTitle = (cardItem as CardLike)?.title ?? undefined;
      const storedSubtitle = (cardItem as CardLike)?.subtitle ?? undefined;
      storedTextRef.current = { title: storedTitle, subtitle: storedSubtitle };
      // Text is stored as HTML, so it is shown decoded once; a subtitle the
      // slide does not show as text (a Cards3 card's colour) is kept as it is.
      shownTextRef.current = {
        title: stripHtml(storedTitle),
        subtitle: showSubtitle
          ? stripHtml(storedSubtitle)
          : storedSubtitle || ''
      };
      setTitle(
        separateTitleAndBody ? shownTextRef.current.title : extractedTitle
      );
      setSubtitle(shownTextRef.current.subtitle);
      if (!separateTitleAndBody && (showTitle || cardOwnsTitle)) {
        setCardTitle(shownTextRef.current.title);
      }
      loadedBodyTitleRef.current = extractedTitle.trim();
      loadedBodyRef.current = rawBodyHtml;
      loadedDescriptionRef.current = stripHtml(workingBodyHtml).replace(
        /^\n+/,
        ''
      );

      setBody(stripHtml(workingBodyHtml).replace(/^\n+/, ''));

      if (answerFeedbackId !== undefined) {
        const answer = cardItem.items?.find(
          it => String(it.id) === String(answerFeedbackId)
        ) as { feedback?: string } | undefined;
        loadedFeedbackRef.current = stripHtml(answer?.feedback || '');
        setFeedback(loadedFeedbackRef.current);
      }

      if (cardItem.pictures && cardItem.pictures[0]) {
        setImageUrl(cardItem.pictures[0].mediaUrl);
        initialImageIdRef.current = cardItem.pictures[0].id;
      } else {
        setImageUrl(emptyImg as unknown as string);
        initialImageIdRef.current = undefined;
      }
    }
  }, [
    cardItem,
    isNew,
    defaultImageUrl,
    separateTitleAndBody,
    showTitle,
    cardOwnsTitle,
    answerFeedbackId
  ]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showFilePicker) {
          setShowFilePicker(false);
        } else {
          closeUnlessSaving();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showFilePicker, onClose, isSaving]);

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    if (isSaving) return;

    if (separateTitleAndBody) {
      if (!title.trim() && !body.trim()) {
        useToastStore.getState().add({
          severity: ToastSeverity.Error,
          message: 'Title or Description is required'
        });
        return;
      }
    }

    const sanitizedBodyContent = sanitizeBodyForSave(body); // preserve internal/leading newlines (leading ones already stripped for display)
    const escapedTitle = escapeHtml(title.trim());

    let combinedBodyHtml = '';

    if (escapedTitle.trim()) {
      combinedBodyHtml =
        `<b>${escapedTitle}</b>` +
        (sanitizedBodyContent ? `<br><br>${sanitizedBodyContent}` : '');
    } else {
      combinedBodyHtml = sanitizedBodyContent ? `${sanitizedBodyContent}` : '';
    }

    if (isNew) {
      if (!sequence) return onClose();
      // The card's own title and a text subtitle are sent escaped (they are
      // stored as HTML); a title taken from the body's heading is sent as is.
      const cardTitleValue = separateTitleAndBody
        ? escapeHtml(title.trim())
        : showTitle
          ? escapeHtml(cardTitle.trim())
          : title;
      // A subtitle the slide does not show as text (a Cards3 card's colour) is
      // sent as it is: never escaped or trimmed.
      const subtitleValue = showSubtitle
        ? escapeHtml(subtitle.trim())
        : subtitle;
      const position =
        (sequence.items?.filter(it => it.__typename === 'Card').length || 0) +
        1;
      const tempId = cardId;
      addSubItem({
        sequenceId: sequence.id,
        data: {
          type: 'CARD',
          title: cardTitleValue,
          value: cardTitleValue,
          subtitle: subtitleValue || undefined,
          body: separateTitleAndBody ? sanitizedBodyContent : combinedBodyHtml,
          position,
          parent_id: null
        },
        newItem: {
          id: tempId,
          position,
          title: cardTitleValue,
          subtitle: subtitleValue || undefined,
          body: separateTitleAndBody ? sanitizedBodyContent : combinedBodyHtml,
          correct: false,
          pictures:
            selectedImageFile && selectedImageFile.id ? [selectedImageFile] : []
        },
        persistFile: !!(selectedImageFile && selectedImageFile.id),
        imageFile:
          selectedImageFile && selectedImageFile.id
            ? (selectedImageFile as { id: string | number })
            : undefined
      });
    } else if (cardItem && questionCard) {
      // Saved on the answer it belongs to, alone, and only when changed.
      if (
        answerFeedbackId !== undefined &&
        feedback !== loadedFeedbackRef.current
      ) {
        setIsSaving(true);
        const result = await updateSubItemBatch(answerFeedbackId, {
          feedback: sanitizeBodyForSave(feedback)
        });
        if (result?.error) {
          setIsSaving(false);
          return;
        }
        markContentEdited();
      }
      if (
        selectedImageFile?.id &&
        selectedImageFile.id !== initialImageIdRef.current
      ) {
        updateFile(cardId.toString(), selectedImageFile!, 'CARD');
      }
    } else if (cardItem) {
      setIsSaving(true);
      const descriptionChanged = body !== loadedDescriptionRef.current;
      const headingChanged =
        !separateTitleAndBody && title.trim() !== loadedBodyTitleRef.current;
      const bodyToSave = descriptionChanged
        ? separateTitleAndBody
          ? sanitizedBodyContent
          : combinedBodyHtml
        : headingChanged
          ? bodyWithHeading(loadedBodyRef.current, escapedTitle)
          : undefined;
      // A changed title or subtitle is sent escaped; one left as it was is
      // sent back as stored, and one the card does not have is not sent.
      const textOf = (field: 'title' | 'subtitle', shown: string) => {
        if (shown.trim() !== shownTextRef.current[field].trim()) {
          return { [field]: escapeHtml(shown.trim()) };
        }
        const stored = storedTextRef.current[field];
        return stored === undefined ? {} : { [field]: stored };
      };
      const result = await updateSubItemBatch(cardId, {
        ...(bodyToSave === undefined ? {} : { body: bodyToSave }),
        ...(titleFromBody
          ? title.trim() === loadedBodyTitleRef.current
            ? {}
            : { title: title.trim() }
          : textOf('title', separateTitleAndBody ? title : cardTitle)),
        // A subtitle the slide does not show as text (a Cards3 card's colour)
        // is sent as it is: never escaped or trimmed.
        ...(showSubtitle
          ? textOf('subtitle', subtitle)
          : storedTextRef.current.subtitle === undefined && !subtitle
            ? {}
            : { subtitle })
      });
      if (result?.error) {
        setIsSaving(false);
        return;
      }
      markContentEdited();

      if (
        selectedImageFile?.id &&
        selectedImageFile.id !== initialImageIdRef.current
      ) {
        updateFile(cardId.toString(), selectedImageFile!, 'CARD');
      }
    }
    onClose();
  };

  const fileElRef = useRef<HTMLDivElement>(null);

  const handleImageSelect = (file: ImageFile) => {
    setSelectedImageFile(file);
    setImageUrl(file.mediaUrl || file.url);
    setShowFilePicker(false);
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <Overlay open={open} opacity={0.5} onClick={closeUnlessSaving}>
      <div
        onClick={e => e.stopPropagation()}
        style={{ position: 'relative', zIndex: 9999 }}
      >
        <StyledModal
          open
          width="520px"
          horizontal="center"
          vertical="center"
          padding="20px 24px 24px"
          minHeight="550px"
        >
          <div style={{ maxHeight: '100vh', overflowY: 'auto' }}>
            <ModalHeader>
              <Title>{isNew ? 'New Card' : 'Edit Card'}</Title>
              <CloseButton
                onClick={closeUnlessSaving}
                aria-label="Close edit card modal"
              >
                <StyledCloseIcon />
              </CloseButton>
            </ModalHeader>
            <form onSubmit={handleSubmit}>
              <Label>Card Image</Label>
              <ImageBox ref={fileElRef}>
                <ImagePreview
                  src={imageUrl || (emptyImg as unknown as string)}
                  alt="Card image"
                />
                <EditImageButton
                  type="button"
                  aria-label="Edit image"
                  // Locked while saving: the save sends the picture chosen before it.
                  disabled={isSaving}
                  onClick={() => setShowFilePicker(true)}
                >
                  <EditBtnIcon />
                </EditImageButton>
              </ImageBox>

              {questionCard && answerFeedbackId !== undefined && (
                <>
                  <Label htmlFor="flip-card-feedback">Feedback</Label>
                  <TextArea
                    id="flip-card-feedback"
                    value={feedback}
                    maxLength={1000}
                    disabled={isSaving}
                    onChange={e => setFeedback(e.target.value)}
                    placeholder="Shown on the back of the card"
                  />
                </>
              )}

              {!questionCard && !separateTitleAndBody && showTitle && (
                <>
                  <Label htmlFor="flip-card-card-title">
                    {titleLabels.cardTitle}
                  </Label>
                  <Input
                    id="flip-card-card-title"
                    value={cardTitle}
                    maxLength={CARD_TITLE_MAX_LENGTH}
                    disabled={isSaving}
                    onChange={e => setCardTitle(e.target.value)}
                    placeholder={titleLabels.cardTitlePlaceholder}
                  />
                </>
              )}

              {!questionCard && showSubtitle && (
                <>
                  <Label htmlFor="flip-card-subtitle">Card Subtitle</Label>
                  <Input
                    id="flip-card-subtitle"
                    value={subtitle}
                    maxLength={120}
                    disabled={isSaving}
                    onChange={e => setSubtitle(e.target.value)}
                    placeholder="Enter subtitle (e.g. @username)"
                  />
                </>
              )}

              {!questionCard && (
                <>
                  <Label htmlFor="flip-card-title">
                    {separateTitleAndBody
                      ? 'Card Title'
                      : titleLabels.bodyTitle}
                  </Label>
                  <Input
                    id="flip-card-title"
                    value={title}
                    maxLength={
                      separateTitleAndBody
                        ? CARD_TITLE_MAX_LENGTH
                        : BODY_TITLE_MAX_LENGTH
                    }
                    disabled={isSaving}
                    onChange={e => setTitle(e.target.value)}
                    placeholder="Enter title"
                  />

                  <Label htmlFor="flip-card-body">Card Description</Label>
                  <TextArea
                    id="flip-card-body"
                    value={body}
                    maxLength={1000}
                    disabled={isSaving}
                    onChange={e => setBody(e.target.value)}
                    placeholder="Enter description"
                  />
                </>
              )}

              <ButtonRow>
                <SubmitButton
                  type="submit"
                  aria-label={isSaving ? 'Saving' : 'Submit'}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving…' : 'Submit'}
                </SubmitButton>
              </ButtonRow>
            </form>
          </div>
        </StyledModal>
        {showFilePicker &&
          ReactDOM.createPortal(
            <div
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 30000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {/* backdrop */}
              <div
                onClick={() => setShowFilePicker(false)}
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'rgba(0,0,0,0.35)'
                }}
              />
              <div
                style={{
                  position: 'relative',
                  maxWidth: '90vw',
                  maxHeight: '90vh',
                  zIndex: 30001,
                  background: 'white',
                  borderRadius: 16,
                  boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
                  overflow: 'hidden'
                }}
              >
                <FilePickerV2
                  updateFile={handleImageSelect}
                  type="image"
                  currentImage={selectedImageFile || cardItem?.pictures?.[0]}
                  fileElRef={fileElRef}
                  imageGenerationSlot={{
                    placement: 'card_image',
                    itemId: cardId,
                    slotKey: 'pictures'
                  }}
                  onClose={() => setShowFilePicker(false)}
                />
              </div>
            </div>,
            document.body
          )}
      </div>
    </Overlay>,
    document.body
  );
};

export default FlipCardEditModal;
